/**
 * Praticiens (bible §5.5 liste + §5.7 fiche 360) — contrat partagé.
 * Le grain praticien×cabinet est central : l'activité d'un praticien dans un
 * cabinet ne masque JAMAIS son silence dans un autre (§3.1).
 *
 * BASCULE DU 2026-08-12 — les lignes sont des DOCTEURS Vevidental (1 120), plus
 * des personnes Twenty (146). La page annonçait 5 travaux sur 6 mois pour le
 * Dr BOUKHRIS Gilles là où la copie API en compte 78, et 54 praticiens sur 146
 * en « jamais commandé ». Deux changements d'ère en découlent :
 *
 *   `idVevidental` devient la clé de ligne, TOUJOURS présente ;
 *   `id` (la fiche Twenty) devient nullable — ~974 docteurs n'en ont pas.
 *
 * Même forme que `CabinetListItemSchema` (`dto.ts`), pour la même raison. Le
 * 11/08, un changement d'ère d'identifiant exactement semblable a rendu
 * l'application inutilisable trois heures : le schéma exigeait encore `Uuid`
 * quand le serveur envoyait déjà des entiers. `contrat-praticiens.test.ts` tient
 * désormais cette couture.
 */
import { z } from "zod";
import { ActionCaseSchema, RELATION_RULE_VERSION } from "./relances.js";
import { SegmentSchema, SyncMetaSchema } from "./dto.js";
import { ECHANGES_VIDES, EchangesSchema } from "./echanges.js";
// Deux copies locales de `Uuid` et `IsoDate` vivaient ici : elles rejoignent la
// définition unique de `primitives.ts` (une règle, un endroit).
import { IdMetier, IsoDate, Uuid } from "./primitives.js";

/** Un rattachement praticien×cabinet avec SON activité propre (§5.7). */
export const RattachementActiviteSchema = z.object({
  /**
   * Fiche cabinet Twenty — `null` quand la clinique n'en a pas encore.
   * 1 126 cliniques sur 1 253 sont dans ce cas : le rattachement existe et se
   * compte, mais il n'y a pas de fiche à ouvrir. Le nier ferait disparaître de
   * la fiche praticien des travaux bien réels.
   */
  cabinetId: Uuid.nullable(),
  /** identifiant Vevidental de la clinique — toujours présent, clé du rattachement */
  cabinetIdVevidental: z.number().int().positive(),
  cabinetNom: z.string(),
  travaux6Mois: z.number().int().min(0),
  dernierTravailLe: IsoDate.nullable(),
  dernierTravailJours: z.number().int().min(0).nullable(),
  /** null = « Historique insuffisant » (§7.A.3 : < 4 travaux / 6 mois). */
  cadenceJours: z.number().positive().nullable(),
  ratio: z.number().min(0).nullable(),
  segment: SegmentSchema,
  ruleVersion: z.literal(RELATION_RULE_VERSION),
});
export type RattachementActivite = z.infer<typeof RattachementActiviteSchema>;

export const PraticienListItemSchema = z.object({
  /** fiche Twenty — `null` tant qu'aucune fiche n'existe pour ce docteur */
  id: Uuid.nullable(),
  /** identifiant Vevidental — clé de ligne, jamais nulle */
  idVevidental: z.number().int().positive(),
  nom: z.string(),
  email: z.string().nullable(),
  telephone: z.string().nullable(),
  /** la case « portable » de Vevidental (`mobile_phone`), à côté du fixe — GO Dove 03/09 */
  mobile: z.string().nullable().default(null),
  cabinetPrincipalNom: z.string().nullable(),
  nbCabinets: z.number().int().min(0),
  dernierTravailJours: z.number().int().min(0).nullable(),
  /** cadence/ratio du rattachement PRINCIPAL (détail par cabinet en dépliant). */
  cadenceJours: z.number().positive().nullable(),
  ratio: z.number().min(0).nullable(),
  segment: SegmentSchema,
  /** Statut humain — non stocké pour les praticiens en V1 : « actif » par défaut, jamais inventé au-delà. */
  statutRelation: z.literal("actif"),
  prochaineActionLe: IsoDate.nullable(),
  responsable: z.string().nullable(),
  rattachements: z.array(RattachementActiviteSchema),
});
export type PraticienListItem = z.infer<typeof PraticienListItemSchema>;

export const PraticiensResponseSchema = z.object({
  items: z.array(PraticienListItemSchema),
  total: z.number().int().min(0),
  /** lignes disposant d'une fiche Twenty — les seules où une note peut s'écrire */
  avecFiche: z.number().int().min(0),
  /**
   * Travaux de la fenêtre 6 mois qui n'indiquent AUCUN docteur, et leur
   * dénominateur.
   *
   * Mesuré le 12/08 : 732 sur 3 915 (19 %). Ils appartiennent à leur cabinet et
   * ne sont jamais répartis entre les praticiens — donc tout compteur par
   * praticien est un MINIMUM, et la page doit le dire. Un « 732 » sans son
   * dénominateur ne voudrait rien dire : les deux voyagent ensemble.
   */
  travauxSansPraticien: z.number().int().min(0),
  travaux6MoisTotal: z.number().int().min(0),
  sync: SyncMetaSchema,
});
export type PraticiensResponse = z.infer<typeof PraticiensResponseSchema>;

export const TravailPraticienSchema = z.object({
  /** identifiant Vevidental du travail (entier en texte), plus un UUID Twenty */
  id: IdMetier,
  code: z.string(),
  statut: z.string().nullable(),
  echeance: IsoDate.nullable(),
  recuLe: IsoDate,
  cabinetId: Uuid.nullable(),
  cabinetIdVevidental: z.number().int().positive().nullable(),
  cabinetNom: z.string().nullable(),
});

export const PraticienFicheSchema = z.object({
  praticien: z.object({
    id: Uuid.nullable(),
    idVevidental: z.number().int().positive(),
    nom: z.string(),
    email: z.string().nullable(),
    telephone: z.string().nullable(),
    mobile: z.string().nullable().default(null),
    statutRelation: z.literal("actif"),
  }),
  rattachements: z.array(RattachementActiviteSchema),
  /**
   * Son fil d'échanges — par SES coordonnées (GO Dove 03/09 : dans 95 % des
   * cas c'est le praticien qui écrit ou appelle). Absent = fil vide, jamais
   * un écran cassé face à un serveur qui ne l'envoie pas encore.
   */
  echanges: EchangesSchema.default(ECHANGES_VIDES),
  travauxRecents: z.array(TravailPraticienSchema),
  actionsOuvertes: z.array(ActionCaseSchema),
  sync: SyncMetaSchema,
});
export type PraticienFiche = z.infer<typeof PraticienFicheSchema>;
